/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpClient,
  HttpBackend,
  HttpHeaders,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  switchMap,
  take,
} from 'rxjs/operators';
import { apiUrl } from '../http/api.constant';
import { Logout } from '../../store/auth/auth.actions';
import { Store } from '@ngxs/store';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { TokenService } from '../authentication/token.service';
import { Router } from '@angular/router';
import { SpinnerService } from '../services/spinner.service';
import { API_URL } from '@shared/service/api.constant';
import { environment } from '../../../environments/environment';
import { AlertService } from '@core/services/alert.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  baseUrl = environment.baseUrl;
  private httpWithoutInterceptor: HttpClient;
  private isResettingSession = false;
  private themeReloadScheduled = false;

  private alert: AlertService;
  private translateService: TranslateService;

  constructor(
    private router: Router,
    private store: Store,
    private tokenService: TokenService,
    private spinner: SpinnerService,
    private injector: Injector,
    private httpBackend: HttpBackend,
  ) {
    // Create HttpClient that doesn't use interceptors to avoid circular dependency
    this.httpWithoutInterceptor = new HttpClient(httpBackend);
  }

  // Lazy load AlertService and TranslateService to break circular dependency
  private get alertService(): AlertService {
    if (!this.alert) {
      this.alert = this.injector.get(AlertService);
    }
    return this.alert;
  }

  private get translator(): TranslateService {
    if (!this.translateService) {
      this.translateService = this.injector.get(TranslateService);
    }
    return this.translateService;
  }

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> =
    new BehaviorSubject<any>(null);
  private readonly refreshFailedMarker = {};

  intercept(
    request: HttpRequest<any> | any,
    next: HttpHandler | any,
  ): Observable<HttpEvent<any>> | any {
    return next.handle(request).pipe(
      //@ts-ignore
      catchError((err: any) => {
        if (this.isMaintenanceMode(err)) {
          this.navigateToMaintenance();
          return;
        }

        if (this.isS11SessionExpired(err)) {
          return this.forceS11Logout(err);
        }

        if (this.isThemeActive401(err, request)) {
          return this.handleThemeRecovery(err);
        }

        if (this.isGeneral401(err, request)) {
          return this.handleUnauthorized(err, request, next);
        }

        this.spinner.hide();
        return throwError(err);
      }),
    );
  }

  private isMaintenanceMode(err: any): boolean {
    return err?.error === 'WEBSITE_IN_MAINTENANCE_MODE';
  }

  private isS11SessionExpired(err: any): boolean {
    return err?.error?.code === 's11_session_expired';
  }

  private forceS11Logout(err: any): Observable<never> {
    this.spinner.hide();
    this.logoutAndRedirect();
    return throwError(err);
  }

  private logoutAndRedirect(): void {
    const s11LoginUrl = localStorage.getItem('s11_login_url');
    this.store.dispatch(new Logout(false, true));
    if (s11LoginUrl) {
      window.location.assign(s11LoginUrl);
    } else {
      this.router.navigate(['/login']);
    }
  }

  private navigateToMaintenance(): void {
    if (!this.router.url.startsWith('/website-maintenance')) {
      this.router.navigate(['/website-maintenance']).then(() => {
        window.location.reload();
      });
    }
  }

  private isThemeActive401(err: any, request: any): boolean {
    return (
      (err.status === 401 || err.status === 0) &&
      request?.url?.includes(API_URL.theme_active)
    );
  }

  private handleThemeRecovery(err: any): Observable<never> {
    const stageKey = 'themeRecoveryStage';
    const stage = sessionStorage.getItem(stageKey);
    const reloadAtKey = 'themeReloadedAt';
    const lastReload = Number(
      sessionStorage.getItem(reloadAtKey) || '0',
    );
    const now = Date.now();
    const cooldownMs = 5000;
    if (now - lastReload < cooldownMs) {
      return throwError(err);
    }
    if (!this.themeReloadScheduled && stage !== 'reloaded') {
      this.themeReloadScheduled = true;
      sessionStorage.setItem(stageKey, 'reloaded');
      sessionStorage.setItem(reloadAtKey, String(now));
      setTimeout(() => {
        window.location.assign(window.location.href);
      }, 50);
      return throwError(err);
    }
    // Already reloaded once in this cycle: do not proceed to general 401 handling
    return throwError(err);
  }

  private isGeneral401(err: any, request: any): boolean {
    return (
      err.status === 401 &&
      request.url !== apiUrl.login &&
      !request.url.includes('token-refresh')
    );
  }

  private handleUnauthorized(err: any, request: any, next: any) {
    const errorCode = err?.error?.code;
    if (errorCode && errorCode === 'concurrent_login') {
      this.spinner.hide();
      Swal.fire({
        title: err.error.title,
        text: err.error.text,
        icon: 'warning',
        width: '550px',
        showCancelButton: false,
        confirmButtonColor: '#3A3A3A',
        confirmButtonText: 'OK',
      }).then(() => {
        // Skip API call since token is already invalid
        this.logoutAndRedirect();
        return throwError(err);
      });
      return throwError(err);
    } else {
      return this.handle401Error(request, next);
    }
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const token = this.tokenService.getRefreshToken();
      if (token) {
        return this.tokenService.refreshToken(token).pipe(
          switchMap((token: any) => {
            this.isRefreshing = false;
            this.tokenService.saveToken(token.access);
            this.refreshTokenSubject.next(token.access);

            return next.handle(
              this.addTokenHeader(request, token.access),
            );
          }),
          // @ts-ignore
          catchError((err) => {
            this.isRefreshing = false;
            this.spinner.hide();
            if (err?.error === 'WEBSITE_IN_MAINTENANCE_MODE') {
              if (
                !this.router.url.startsWith('/website-maintenance')
              ) {
                this.router
                  .navigate(['/website-maintenance'])
                  .then(() => {
                    window.location.reload();
                  });
              }
              return;
            }

            // Skip API call since refresh token has already failed
            this.logoutAndRedirect();
            this.refreshTokenSubject.next(this.refreshFailedMarker);
            return throwError(err);
          }),
        );
      } else {
        if (!this.router.url.includes('/login')) {
          this.isRefreshing = false;
          this.spinner.hide();
          // Skip API call since there's no valid refresh token
          this.logoutAndRedirect();
        }
        this.refreshTokenSubject.next(this.refreshFailedMarker);
        return;
      }
    }
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        if (token === this.refreshFailedMarker) {
          return throwError(
            () =>
              new Error('Token refresh failed for a queued request'),
          );
        }
        return next.handle(this.addTokenHeader(request, token));
      }),
    );
  }

  resetSession() {
    const userId = sessionStorage.getItem('id');
    const access = sessionStorage.getItem('access');
    if (this.isResettingSession || !userId || !access) {
      return;
    }
    this.isResettingSession = true;
    this.spinner.show();
    const request = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${access}`,
      }),
    };

    this.httpWithoutInterceptor
      .post(
        this.baseUrl + API_URL.reset_session,
        {
          user_id: userId,
        },
        request,
      )
      .pipe(
        finalize(() => {
          this.router.navigate(['/login']);
          this.spinner.hide();
          this.isResettingSession = false;
        }),
      )
      .subscribe({
        next: () => {
          Swal.fire({
            title: 'Session Expired',
            text: 'Your session has expired. Please log in again to continue.',
            icon: 'info',
            width: '550px',
            showCancelButton: false,
            confirmButtonColor: '#3A3A3A',
            confirmButtonText: 'OK',
          });
        },
        error: (error: any) => {
          if (error?.error?.detail.includes('token not valid')) {
            this.alertService.error(
              this.translator.instant(
                'SESSION-TIMEOUT.SESSION-TIMED-OUT-LOGIN-AGAIN',
              ),
            );
          }
        },
      });
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
